import { useEffect, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import toast from 'react-hot-toast';
import { fetchDefaultGradeSchemaForSubject, fetchGradeSchemas, previewGradeSchemaMatch } from '../../lib/classworkApi';

export type ClassworkFinalizePayload = {
  points_achieved?: number;
  points_max?: number;
  score_percent?: number;
  /** Optional, wenn Schema + Prozent und Server die Note ableitet */
  grade_value?: string;
  grade_schema_id?: number;
  apply_grade_from_schema?: boolean;
  teacher_feedback?: string;
  teacher_override?: boolean;
};

type Props = {
  onFinalize: (payload: ClassworkFinalizePayload) => void;
  /** Fach der Klassenarbeit → Standard-Notenschema vorausfüllen */
  classworkSubjectId?: number | null;
  /** Im Wizard festgelegtes Schema – hat Vorrang vor Fach-Standard */
  boundGradeSchemaId?: number | null;
};

export default function ClassworkCorrectionReview({ onFinalize, classworkSubjectId, boundGradeSchemaId }: Props) {
  const [pointsAchieved, setPointsAchieved] = useState<string>('');
  const [pointsMax, setPointsMax] = useState<string>('');
  const [scorePercent, setScorePercent] = useState<string>('');
  const [gradeValue, setGradeValue] = useState<string>('');
  const [feedback, setFeedback] = useState<string>('');
  const [schemaId, setSchemaId] = useState<string>('');
  const [applyFromSchema, setApplyFromSchema] = useState(true);
  const [showAdvancedScoring, setShowAdvancedScoring] = useState(false);
  const didPrefillSchema = useRef(false);

  const schemasQuery = useQuery(['classwork-grade-schemas'], () => fetchGradeSchemas());
  const defaultForSubjectQuery = useQuery(
    ['grade-default-subject', classworkSubjectId],
    () => fetchDefaultGradeSchemaForSubject(classworkSubjectId!),
    { enabled: classworkSubjectId != null && classworkSubjectId > 0 }
  );

  useEffect(() => {
    didPrefillSchema.current = false;
  }, [classworkSubjectId, boundGradeSchemaId]);

  useEffect(() => {
    if (boundGradeSchemaId != null && boundGradeSchemaId > 0) {
      setSchemaId(String(boundGradeSchemaId));
      didPrefillSchema.current = true;
      return;
    }
    if (didPrefillSchema.current) return;
    const gid = defaultForSubjectQuery.data?.grade_schema_id;
    if (gid == null) return;
    if (schemaId !== '') return;
    setSchemaId(String(gid));
    didPrefillSchema.current = true;
  }, [boundGradeSchemaId, defaultForSubjectQuery.data, schemaId]);

  const runPreview = async () => {
    const sid = schemaId ? Number(schemaId) : NaN;
    const pct = scorePercent ? Number(scorePercent) : NaN;
    if (!sid || Number.isNaN(sid)) {
      toast.error('Bitte Notenschema wählen');
      return;
    }
    if (Number.isNaN(pct) || pct < 0 || pct > 100) {
      toast.error('Bitte gültige Prozentzahl (0–100) eintragen');
      return;
    }
    try {
      const data = await previewGradeSchemaMatch(sid, pct);
      if (data?.grade_value != null) {
        setGradeValue(String(data.grade_value));
        toast.success('Vorschau übernommen');
      } else {
        toast.error('Keine Note aus Schema ermittelbar');
      }
    } catch {
      toast.error('Vorschau fehlgeschlagen');
    }
  };

  const handleFinalize = () => {
    const sid = schemaId ? Number(schemaId) : undefined;
    const pct = scorePercent === '' ? undefined : scorePercent ? Number(scorePercent) : undefined;
    const hasSchemaApply = Boolean(sid && pct != null && !Number.isNaN(pct) && applyFromSchema);
    const gv = String(gradeValue || '').trim();
    if (!hasSchemaApply && !gv) {
      toast.error('Note eintragen oder Schema + Prozent mit „aus Schema ableiten“ nutzen');
      return;
    }
    const payload: ClassworkFinalizePayload = {
      points_achieved: pointsAchieved ? Number(pointsAchieved) : undefined,
      points_max: pointsMax ? Number(pointsMax) : undefined,
      score_percent: pct,
      grade_schema_id: sid,
      apply_grade_from_schema: applyFromSchema,
      teacher_feedback: feedback,
      teacher_override: true
    };
    if (gv) payload.grade_value = gv;
    onFinalize(payload);
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <h3 className="text-lg font-semibold text-gray-900 mb-1">Bewertung finalisieren</h3>
      <p className="text-xs text-gray-600 mb-3">Standard: Note und Feedback setzen. Schema-/Punkte-Logik ist optional.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <input className="input-field" placeholder="Note / Bewertungstext" value={gradeValue} onChange={(e) => setGradeValue(e.target.value)} />
        <textarea className="input-field min-h-[80px]" placeholder="Feedback" value={feedback} onChange={(e) => setFeedback(e.target.value)} />
      </div>
      <div className="mt-3">
        <button
          type="button"
          className="text-xs font-medium text-gray-700 underline"
          onClick={() => setShowAdvancedScoring((prev) => !prev)}
        >
          {showAdvancedScoring ? 'Erweiterte Bewertung ausblenden' : 'Erweiterte Bewertung anzeigen'}
        </button>
        {showAdvancedScoring ? (
          <div className="mt-3 space-y-3 rounded-lg border border-gray-100 bg-gray-50/60 p-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <input className="input-field" placeholder="Punkte erreicht" value={pointsAchieved} onChange={(e) => setPointsAchieved(e.target.value)} />
              <input className="input-field" placeholder="Max Punkte" value={pointsMax} onChange={(e) => setPointsMax(e.target.value)} />
              <input className="input-field" placeholder="Prozent (0–100)" value={scorePercent} onChange={(e) => setScorePercent(e.target.value)} />
            </div>
            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 items-start sm:items-end">
              <div className="flex-1 min-w-[200px]">
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Notenschema {boundGradeSchemaId ? '(aus Klassenarbeit)' : '(optional)'}
                </label>
                <select
                  className="input-field text-sm"
                  value={schemaId}
                  onChange={(e) => setSchemaId(e.target.value)}
                  disabled={schemasQuery.isLoading || (boundGradeSchemaId != null && boundGradeSchemaId > 0)}
                >
                  <option value="">— keines —</option>
                  {(schemasQuery.data || []).map((s: { id: number; name: string }) => (
                    <option key={s.id} value={String(s.id)}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input type="checkbox" checked={applyFromSchema} onChange={(e) => setApplyFromSchema(e.target.checked)} />
                Note beim Speichern aus Schema + Prozent ableiten
              </label>
              <button type="button" className="btn-secondary text-sm" onClick={() => void runPreview()}>
                Vorschau aus Schema
              </button>
            </div>
          </div>
        ) : null}
      </div>
      <button className="btn-primary mt-3" type="button" onClick={handleFinalize}>
        Bewertung final speichern
      </button>
    </div>
  );
}
